import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FilePen, PlusCircle, Save, Trash2, Loader2, Eye, EyeOff } from "lucide-react";
import { api } from "@/lib/api";

// Macrovoci (categorie) fisse del catalogo servizi contratto.
// L'opzione vuota = "Altri servizi" (nessuna categoria), rimane per retrocompatibilità
// con i vecchi servizi legacy senza categoria assegnata.
const CATEGORIES = [
  "1. BRANDING E POSIZIONAMENTO",
  "2. PROMOZIONE STRATEGICA (UFFICIO STAMPA E RADIO)",
  "3. MONETIZZAZIONE E SCALABILITÀ",
  "4. PRODUZIONE E CREAZIONE DEI BRANI",
];
const NO_CAT_LABEL = "Altri servizi (senza categoria)";

export default function AdminServiziContratto() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newCategory, setNewCategory] = useState(CATEGORIES[0]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/contract-services");
      setItems(r.data || []);
    } catch (e) {
      toast.error("Errore caricamento");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const addService = async () => {
    if (!newLabel.trim()) return;
    setSaving(true);
    try {
      const r = await api.post("/admin/contract-services", {
        label: newLabel.trim(),
        category: newCategory || "",
      });
      setItems((it) => [...it, r.data]);
      setNewLabel("");
      toast.success("Servizio aggiunto");
    } catch (e) {
      toast.error("Errore");
    } finally {
      setSaving(false);
    }
  };

  const [seeding, setSeeding] = useState(false);
  const seedMacrovoci = async () => {
    if (!window.confirm(
      "Rigenerare il catalogo con le 4 Macrovoci ufficiali?\n\n" +
      "Operazione idempotente: aggiunge le voci mancanti e aggiorna categorie di quelle esistenti.\n" +
      "I 15 servizi legacy 'Altri servizi' NON vengono toccati."
    )) return;
    setSeeding(true);
    try {
      const r = await api.post("/admin/contract-services/seed-macrovoci");
      toast.success(`Catalogo aggiornato: +${r.data.inserted} nuovi, ${r.data.updated} ricategorizzati. Totale: ${r.data.total_services}`);
      await load();
    } catch (e) {
      toast.error("Errore rigenerazione catalogo");
    } finally {
      setSeeding(false);
    }
  };

  const updateService = async (id, patch) => {
    try {
      await api.patch(`/admin/contract-services/${id}`, patch);
      setItems((it) => it.map((s) => (s.id === id ? { ...s, ...patch } : s)));
      toast.success("Aggiornato");
    } catch (e) {
      toast.error("Errore aggiornamento");
    }
  };

  const deleteService = async (id) => {
    if (!window.confirm("Eliminare questo servizio?")) return;
    try {
      await api.delete(`/admin/contract-services/${id}`);
      setItems((it) => it.filter((s) => s.id !== id));
      toast.success("Eliminato");
    } catch (e) {
      toast.error("Errore");
    }
  };

  // Raggruppa i servizi per categoria. Mantiene ordine fisso delle Macrovoci,
  // mette "Altri servizi" in fondo.
  const grouped = useMemo(() => {
    const map = {};
    items.forEach((s) => {
      const cat = (s.category || "").trim() || NO_CAT_LABEL;
      if (!map[cat]) map[cat] = [];
      map[cat].push(s);
    });
    const ordered = [];
    CATEGORIES.forEach((c) => { if (map[c]) ordered.push([c, map[c]]); });
    // Qualunque categoria sconosciuta (non in CATEGORIES) a seguire
    Object.keys(map).forEach((c) => {
      if (!CATEGORIES.includes(c) && c !== NO_CAT_LABEL) ordered.push([c, map[c]]);
    });
    if (map[NO_CAT_LABEL]) ordered.push([NO_CAT_LABEL, map[NO_CAT_LABEL]]);
    return ordered;
  }, [items]);

  return (
    <div className="p-6 lg:p-10 max-w-[1100px] mx-auto" data-testid="servizi-contratto-page">
      <header className="mb-8">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#FF5A1F]">
          <FilePen size={14} /> Admin Privato
        </div>
        <div className="mt-2 flex items-start justify-between gap-4 flex-wrap">
          <h1 className="font-display text-4xl md:text-5xl uppercase text-[#0A0A0A]">Servizi contratto</h1>
          <button
            onClick={seedMacrovoci}
            disabled={seeding}
            className="btn-cta disabled:opacity-60 whitespace-nowrap"
            data-testid="seed-macrovoci-btn"
            title="Popola/aggiorna il catalogo con le 4 Macrovoci ufficiali del contratto Perla Music"
          >
            {seeding ? <Loader2 size={14} className="animate-spin" /> : <PlusCircle size={14} />}
            Rigenera 4 Macrovoci
          </button>
        </div>
        <p className="mt-2 text-sm text-[#0A0A0A]/60 max-w-3xl">
          Catalogo dei servizi disponibili nel modulo Contratti, raggruppati per <b>Macrovoce</b>.
          Nel form del contratto verranno mostrati come checkbox da spuntare, raggruppati per
          Macrovoce. Puoi attivare/disattivare, modificare il testo o cambiare Macrovoce per
          ciascun servizio.
          <br/>
          <b>Nuovo catalogo?</b> Clicca <i>"Rigenera 4 Macrovoci"</i> in alto a destra per popolare in
          una volta sola i 19 servizi ufficiali.
        </p>
      </header>

      {/* Add new */}
      <div className="border-2 border-[#0A0A0A] bg-[#F2F2EC] p-4 mb-8" data-testid="add-service-card">
        <label className="text-[10px] uppercase tracking-widest font-bold text-[#FF5A1F] mb-2 block">
          Aggiungi nuovo servizio
        </label>
        <div className="grid md:grid-cols-[1fr_320px_auto] gap-2 items-start">
          <textarea
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="Testo del servizio (es. 'Realizzazione di uno shooting fotografico ...')"
            rows={2}
            className="w-full border-2 border-[#0A0A0A] p-2 text-sm resize-none focus:outline-none focus:border-[#FF5A1F]"
            data-testid="new-service-input"
          />
          <select
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            className="w-full border-2 border-[#0A0A0A] p-2 text-sm bg-white focus:outline-none focus:border-[#FF5A1F]"
            data-testid="new-service-category"
          >
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            <option value="">— Nessuna categoria (Altri servizi) —</option>
          </select>
          <button
            onClick={addService}
            disabled={saving || !newLabel.trim()}
            className="btn-cta disabled:opacity-60 whitespace-nowrap self-start"
            data-testid="add-service-btn"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <PlusCircle size={14} />}
            Aggiungi
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-12 text-center text-sm text-[#0A0A0A]/50">
          <Loader2 size={20} className="animate-spin inline" />
        </div>
      ) : items.length === 0 ? (
        <div className="border-2 border-dashed border-[#0A0A0A]/30 p-8 text-center text-sm text-[#0A0A0A]/50">
          Nessun servizio ancora. Aggiungine uno sopra.
        </div>
      ) : (
        <div className="space-y-8">
          {grouped.map(([cat, list]) => (
            <section key={cat} data-testid={`cat-section-${cat.replace(/\s+/g,"-")}`}>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm uppercase tracking-widest font-bold text-[#0A0A0A] bg-[#FF5A1F] text-white px-3 py-1 inline-block">
                  {cat}
                </h2>
                <div className="text-[10px] text-[#0A0A0A]/50 uppercase tracking-widest">
                  {list.length} servizi
                </div>
              </div>
              <div className="space-y-2">
                {list.map((s, idx) => (
                  <ServiceRow
                    key={s.id}
                    service={s}
                    index={idx + 1}
                    onUpdate={updateService}
                    onDelete={deleteService}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <div className="mt-8 text-[10px] text-[#0A0A0A]/40 uppercase tracking-widest">
        Totale: {items.length} · Attivi: {items.filter((i) => i.active !== false).length}
      </div>
    </div>
  );
}

function ServiceRow({ service, index, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(service.label);
  const [category, setCategory] = useState(service.category || "");
  const isActive = service.active !== false;
  const saveEdit = () => {
    const patch = {};
    if (label.trim() !== service.label) patch.label = label.trim();
    if ((category || "") !== (service.category || "")) patch.category = category || "";
    if (Object.keys(patch).length > 0) onUpdate(service.id, patch);
    setEditing(false);
  };
  return (
    <div className={`border-2 border-[#0A0A0A] p-4 flex items-start gap-3 ${isActive ? "bg-[#FAFAF7]" : "bg-[#F2F2EC] opacity-70"}`} data-testid={`service-row-${service.id}`}>
      <div className="text-[11px] uppercase tracking-widest font-bold text-[#FF5A1F] w-8 pt-1 shrink-0">
        {String(index).padStart(2, "0")}
      </div>
      <div className="flex-1">
        {editing ? (
          <div className="space-y-2">
            <textarea
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              rows={2}
              className="w-full border-2 border-[#0A0A0A] p-2 text-sm resize-none focus:outline-none focus:border-[#FF5A1F]"
              data-testid={`service-edit-input-${service.id}`}
              autoFocus
            />
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full border-2 border-[#0A0A0A] p-2 text-sm bg-white focus:outline-none focus:border-[#FF5A1F]"
              data-testid={`service-edit-category-${service.id}`}
            >
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              <option value="">— Nessuna categoria (Altri servizi) —</option>
            </select>
          </div>
        ) : (
          <>
            <div className="text-sm text-[#0A0A0A]/85 leading-snug">{service.label}</div>
            {service.category && (
              <div className="mt-1 text-[9px] uppercase tracking-widest text-[#FF5A1F] font-bold">
                {service.category}
              </div>
            )}
          </>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {editing ? (
          <>
            <button
              onClick={saveEdit}
              className="px-2 py-1 border-2 border-[#0A0A0A] text-[10px] uppercase tracking-widest hover:bg-[#FF5A1F] hover:border-[#FF5A1F] hover:text-white"
              data-testid={`service-save-${service.id}`}
            >
              <Save size={12} className="inline" /> Salva
            </button>
            <button
              onClick={() => { setLabel(service.label); setCategory(service.category || ""); setEditing(false); }}
              className="text-[10px] text-[#0A0A0A]/50 uppercase tracking-widest px-2"
              data-testid={`service-cancel-${service.id}`}
            >
              Annulla
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => onUpdate(service.id, { active: !isActive })}
              className={`p-2 border-2 ${isActive ? "border-green-600 text-green-600" : "border-[#0A0A0A]/30 text-[#0A0A0A]/40"} hover:bg-[#F2F2EC]`}
              title={isActive ? "Attivo — clic per disattivare" : "Disattivo — clic per attivare"}
              data-testid={`service-toggle-${service.id}`}
            >
              {isActive ? <Eye size={12} /> : <EyeOff size={12} />}
            </button>
            <button
              onClick={() => setEditing(true)}
              className="px-2 py-1 border-2 border-[#0A0A0A]/30 text-[10px] uppercase tracking-widest hover:border-[#FF5A1F] hover:text-[#FF5A1F]"
              data-testid={`service-edit-${service.id}`}
            >
              Modifica
            </button>
            <button
              onClick={() => onDelete(service.id)}
              className="p-2 border-2 border-[#0A0A0A]/30 text-[#0A0A0A]/40 hover:border-red-500 hover:text-red-500"
              title="Elimina"
              data-testid={`service-delete-${service.id}`}
            >
              <Trash2 size={12} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
